/**
 * Payroll Processing Page
 * Monthly payroll runs with Ghana statutory compliance
 */

import React, { useState, useEffect } from 'react'
import { Plus, Calendar, CheckCircle, DollarSign, Users, FileText, Download, Send, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import PayrollService, { PayrollRun, PayrollDetail } from '@/services/payrollService'
import PayrollAccountingService from '@/services/payrollAccountingService'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface PayrollProcessingProps {
  user: AdminUser | null
}

export default function PayrollProcessing({ user }: PayrollProcessingProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(user?.churchId)
  const [payrollRuns, setPayrollRuns] = useState<PayrollRun[]>([])
  const [selectedRun, setSelectedRun] = useState<PayrollRun | null>(null)
  const [payrollDetails, setPayrollDetails] = useState<PayrollDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)

  // New payroll form
  const currentDate = new Date()
  const [newPayrollMonth, setNewPayrollMonth] = useState(currentDate.getMonth() + 1)
  const [newPayrollYear, setNewPayrollYear] = useState(currentDate.getFullYear())

  useEffect(() => {
    if (user?.churchId) {
      loadPayrollRuns()
    }
  }, [user])

  async function loadPayrollRuns() {
    if (!user?.churchId) return

    try {
      setLoading(true)
      const data = await PayrollService.getPayrollRuns(user.churchId)
      setPayrollRuns(data)
    } catch (error) {
      console.error('Error loading payroll runs:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleCreatePayroll() {
    if (!user?.churchId || !user.id) return

    setProcessing(true)
    try {
      const result = await PayrollService.createPayrollRun(
        user.churchId,
        newPayrollMonth,
        newPayrollYear,
        user.id
      )

      if (result.success) {
        alert('Success: Payroll run created')
        setShowCreateDialog(false)
        loadPayrollRuns()
      } else {
        alert(`Error: ${result.error}`)
      }
    } catch (error) {
      console.error('Error creating payroll:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to create payroll'}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handleCalculatePayroll(payrollRun: PayrollRun) {
    if (!user?.churchId) return

    if (!confirm(`Calculate payroll for ${getMonthName(payrollRun.payroll_month)} ${payrollRun.payroll_year}?`)) {
      return
    }

    setProcessing(true)
    try {
      const result = await PayrollService.calculatePayrollForAllEmployees(
        payrollRun.id,
        user.churchId
      )

      if (result.success) {
        alert(`Success: Payroll calculated for ${result.employeesProcessed} employees`)
        loadPayrollRuns()
      } else {
        alert(`Error: ${result.error}`)
      }
    } catch (error) {
      console.error('Error calculating payroll:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to calculate payroll'}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handleViewDetails(payrollRun: PayrollRun) {
    setSelectedRun(payrollRun)
    setProcessing(true)
    try {
      const details = await PayrollService.getPayrollDetails(payrollRun.id)
      setPayrollDetails(details)
      setShowDetailsDialog(true)
    } catch (error) {
      console.error('Error loading payroll details:', error)
      alert('Error: Failed to load payroll details')
    } finally {
      setProcessing(false)
    }
  }

  async function handleApprovePayroll(payrollRun: PayrollRun) {
    if (!user?.id) return

    if (!confirm(`Approve payroll for ${getMonthName(payrollRun.payroll_month)} ${payrollRun.payroll_year}?`)) {
      return
    }

    setProcessing(true)
    try {
      const result = await PayrollService.approvePayrollRun(payrollRun.id, user.id)

      if (result.success) {
        alert('Success: Payroll approved')
        loadPayrollRuns()
      } else {
        alert(`Error: ${result.error}`)
      }
    } catch (error) {
      console.error('Error approving payroll:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to approve payroll'}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handleMarkAsPaid(payrollRun: PayrollRun) {
    if (!user?.id) return

    const paymentDate = prompt('Enter payment date (YYYY-MM-DD):', new Date().toISOString().split('T')[0])
    if (!paymentDate) return

    setProcessing(true)
    try {
      const result = await PayrollService.markPayrollAsPaid(payrollRun.id, paymentDate, user.id)

      if (result.success) {
        alert('Success: Payroll marked as paid')
        loadPayrollRuns()
      } else {
        alert(`Error: ${result.error}`)
      }
    } catch (error) {
      console.error('Error marking payroll as paid:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to mark as paid'}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handlePostToGL(payrollRun: PayrollRun) {
    if (!user?.churchId || !user.id) return

    if (!confirm(`Post payroll to General Ledger for ${getMonthName(payrollRun.payroll_month)} ${payrollRun.payroll_year}?\n\nThis will create journal entries in the accounting system.`)) {
      return
    }

    setProcessing(true)
    try {
      // Get GL account settings
      const accounts = await PayrollAccountingService.getPayrollGLAccounts(user.churchId)

      if (!accounts) {
        alert('Error: GL accounts not configured.\n\nPlease configure GL account mappings in the Chart of Accounts first.')
        return
      }

      // Post to GL
      const result = await PayrollAccountingService.postPayrollToGL(
        payrollRun.id,
        user.churchId,
        user.id,
        accounts
      )

      if (result.success) {
        alert(`Success: Payroll posted to General Ledger\n\nJournal Entry ID: ${result.journalEntryId}\n\nYou can view the journal entry in the Finance > Journal Entries page.`)
        loadPayrollRuns()
      } else {
        alert(`Error posting to GL: ${result.error}`)
      }
    } catch (error) {
      console.error('Error posting to GL:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to post to GL'}`)
    } finally {
      setProcessing(false)
    }
  }

  function getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    return months[month - 1]
  }

  function getStatusColor(status: string): 'default' | 'secondary' | 'destructive' {
    switch (status) {
      case 'paid':
      case 'posted':
        return 'default'
      case 'approved':
        return 'secondary'
      case 'draft':
      case 'calculated':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  if (!features.hrPayroll) {
    return (
      <FeatureRestricted
        featureName="Payroll Processing"
        description="Process church staff payroll with automated tax calculations, deductions, payslip generation, and bank file exports."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="leadership"
        icon={<Wallet className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Payroll Processing</h1>
            <p className="text-sm text-muted-foreground">
              Monthly payroll runs with Ghana statutory compliance
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Payroll Run
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Payroll Runs List */}
        <Card>
          <CardHeader>
            <CardTitle>Payroll Runs</CardTitle>
            <CardDescription>
              {payrollRuns.length} payroll runs found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {payrollRuns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="mx-auto h-12 w-12 mb-4 opacity-50" />
                <p>No payroll runs yet</p>
                <p className="text-sm">Create your first payroll run to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead className="text-right">Gross Pay</TableHead>
                    <TableHead className="text-right">SSNIT</TableHead>
                    <TableHead className="text-right">PAYE</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollRuns.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell className="font-medium">
                        {getMonthName(run.payroll_month)} {run.payroll_year}
                      </TableCell>
                      <TableCell>{run.total_employees}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(run.total_gross, 'GHS')}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(run.total_employee_ssnit + run.total_employer_ssnit, 'GHS')}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(run.total_paye, 'GHS')}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(run.total_net_pay, 'GHS')}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(run.status)}>
                          {run.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {run.status === 'draft' && (
                            <Button
                              size="sm"
                              onClick={() => handleCalculatePayroll(run)}
                              disabled={processing}
                            >
                              Calculate
                            </Button>
                          )}
                          {run.status === 'calculated' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDetails(run)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleApprovePayroll(run)}
                                disabled={processing}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                            </>
                          )}
                          {run.status === 'approved' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDetails(run)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleMarkAsPaid(run)}
                                disabled={processing}
                              >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Mark Paid
                              </Button>
                            </>
                          )}
                          {run.status === 'paid' && !run.is_posted_to_gl && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDetails(run)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handlePostToGL(run)}
                                disabled={processing}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Post to GL
                              </Button>
                            </>
                          )}
                          {(run.status === 'posted' || (run.status === 'paid' && run.is_posted_to_gl)) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDetails(run)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              {run.is_posted_to_gl && (
                                <Badge variant="default" className="ml-2">
                                  Posted to GL
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Payroll Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Payroll Run</DialogTitle>
            <DialogDescription>
              Select the month and year for this payroll run
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="month">Month</Label>
              <Select
                value={newPayrollMonth.toString()}
                onValueChange={(value) => setNewPayrollMonth(parseInt(value))}
              >
                <SelectTrigger id="month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <SelectItem key={month} value={month.toString()}>
                      {getMonthName(month)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="year">Year</Label>
              <Select
                value={newPayrollYear.toString()}
                onValueChange={(value) => setNewPayrollYear(parseInt(value))}
              >
                <SelectTrigger id="year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 1 + i).map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleCreatePayroll} disabled={processing}>
              {processing ? 'Creating...' : 'Create Payroll Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Payroll Details - {selectedRun && `${getMonthName(selectedRun.payroll_month)} ${selectedRun.payroll_year}`}
            </DialogTitle>
            <DialogDescription>
              Review individual payslips
            </DialogDescription>
          </DialogHeader>

          {selectedRun && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Employees</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{selectedRun.total_employees}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Gross Pay</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(selectedRun.total_gross, 'GHS')}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Total Deductions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(selectedRun.total_deductions, 'GHS')}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      SSNIT: {formatCurrency(selectedRun.total_employee_ssnit, 'GHS')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PAYE: {formatCurrency(selectedRun.total_paye, 'GHS')}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Net Pay</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(selectedRun.total_net_pay, 'GHS')}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Employer SSNIT: {formatCurrency(selectedRun.total_employer_ssnit, 'GHS')}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Payslips Table */}
              <div>
                <h3 className="font-semibold mb-4">Individual Payslips</h3>
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="text-right">Basic</TableHead>
                        <TableHead className="text-right">Allowances</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">SSNIT</TableHead>
                        <TableHead className="text-right">PAYE</TableHead>
                        <TableHead className="text-right">Deductions</TableHead>
                        <TableHead className="text-right">Net Pay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payrollDetails.map((detail) => (
                        <TableRow key={detail.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{detail.employee_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {detail.employee_code} - {detail.position_title}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(detail.basic_salary, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(
                              detail.housing_allowance +
                              detail.transport_allowance +
                              detail.utility_allowance +
                              detail.meal_allowance +
                              detail.other_allowances,
                              'GHS'
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(detail.gross_pay, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(detail.employee_ssnit, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(detail.paye_tax, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(detail.total_deductions, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(detail.net_pay, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
